import axios from 'axios';
import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { CapacitorHttp } from '@capacitor/core';
import { useAuthStore } from '../stores/authStore';
import { isNativePlatform } from '../utils/platform';

function getApiBaseUrl(): string {
  if (isNativePlatform()) {
    return import.meta.env.VITE_MOBILE_API_URL || 'https://neimus.ai/api/v1';
  }

  if (import.meta.env.VITE_API_URL) return import.meta.env.VITE_API_URL;

  return '/api/v1';
}

const API_BASE_URL = getApiBaseUrl();

const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  // Use fetch adapter in Capacitor so CapacitorHttp can intercept and
  // route requests through the native HTTP layer (bypasses CORS/mixed-content).
  ...(isNativePlatform() ? { adapter: 'fetch' as const } : {}),
});

// Audit Finding #H9: tokens are no longer stored in the persisted
// `neimus360-auth` localStorage snapshot. They live in sessionStorage
// (web) or localStorage (Capacitor native) under
// `neimus360-auth-tokens`. We read the same keys the authStore writes.
const TOKEN_STORAGE_KEY = 'neimus360-auth-tokens';

function getTokenStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return isNativePlatform() ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function getStoredAuth(): { accessToken: string | null; refreshToken: string | null } {
  // First try the Zustand store
  const state = useAuthStore.getState();
  if (state.accessToken) {
    return { accessToken: state.accessToken, refreshToken: state.refreshToken };
  }

  // Fallback to the token storage (handles race condition after login).
  try {
    const storage = getTokenStorage();
    if (storage) {
      const raw = storage.getItem(TOKEN_STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          accessToken: parsed?.accessToken ?? null,
          refreshToken: parsed?.refreshToken ?? null,
        };
      }
    }
  } catch {
    // Ignore parse errors
  }
  return { accessToken: null, refreshToken: null };
}

// Request interceptor - add auth token and remove trailing slashes
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Remove trailing slashes to prevent 307 redirects
    // FastAPI expects URLs without trailing slashes
    if (config.url && config.url.endsWith('/') && config.url.length > 1) {
      config.url = config.url.slice(0, -1);
    }
    
    // Skip auth for login endpoint
    if (config.url?.includes('/auth/login')) {
      return config;
    }

    const { accessToken } = getStoredAuth();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Audit Finding #H9: shared refresh lock. Without this, N parallel
// requests that 401 at the same time would each fire their own
// /auth/refresh call. Every call rotates the refresh token, so all
// but the first refresh would fail with "invalid refresh token" and
// log the user out — even though the first refresh succeeded.
let refreshPromise: Promise<string> | null = null;

async function performTokenRefresh(refreshToken: string): Promise<string> {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    try {
      let access_token: string;
      let newRefreshToken: string;

      if (isNativePlatform()) {
        const result = await CapacitorHttp.post({
          url: `${API_BASE_URL}/auth/refresh`,
          headers: { 'Content-Type': 'application/json' },
          data: { refresh_token: refreshToken },
        });
        if (result.status >= 400) {
          throw new Error(`Refresh failed (${result.status})`);
        }
        access_token = result.data.access_token;
        newRefreshToken = result.data.refresh_token;
      } else {
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        });
        access_token = response.data.access_token;
        newRefreshToken = response.data.refresh_token;
      }

      useAuthStore.getState().setTokens(access_token, newRefreshToken);
      return access_token;
    } finally {
      // Release the lock either way so the next 401 cycle can refresh.
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// Response interceptor - handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    const isAuthEndpoint = originalRequest.url?.includes('/auth/login') ||
                           originalRequest.url?.includes('/auth/forgot-password') ||
                           originalRequest.url?.includes('/auth/reset-password') ||
                           originalRequest.url?.includes('/auth/recover') ||
                           originalRequest.url?.includes('/auth/2fa/verify') ||
                           originalRequest.url?.includes('/auth/refresh');

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      originalRequest._retry = true;

      const { refreshToken } = getStoredAuth();
      if (refreshToken) {
        try {
          const access_token = await performTokenRefresh(refreshToken);
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          useAuthStore.getState().logout();
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        }
      } else {
        useAuthStore.getState().logout();
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;

// Normalize a paginated response so `total_pages` is always populated.
// Several backend list endpoints return the page count as `pages`; the
// frontend types/components read `total_pages`. Without this, pagination
// controls think there is only ever one page and rows beyond the first
// page become unreachable.
export function normalizePaginated<T>(data: any): T {
  if (
    data &&
    typeof data === 'object' &&
    data.total_pages === undefined &&
    typeof data.pages === 'number'
  ) {
    return { ...data, total_pages: data.pages };
  }
  return data;
}

// Utility function to handle API errors
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{
      detail?:
        | string
        | { code?: string; message?: string }
        | Array<{ msg?: string; message?: string; detail?: string }>;
      error?: string;
      message?: string;
      retry_after_seconds?: number;
    }>;
    const data = axiosError.response?.data;

    // Rate-limit responses (429) get special handling.
    if (axiosError.response?.status === 429) {
      if (data?.message) return data.message;
      if (typeof data?.retry_after_seconds === 'number') {
        return `Too many requests. Please try again in ${data.retry_after_seconds} seconds.`;
      }
      return 'Too many requests. Please wait a minute and try again.';
    }

    // Check for FastAPI's default format (detail). Some endpoints send a
    // structured detail object ({code, message}) — unwrap the message so
    // callers never render "[object Object]".
    if (data?.detail) {
      if (typeof data.detail === 'string') {
        return data.detail;
      }
      if (Array.isArray(data?.detail)) {
        const details = data.detail
          .map((item) => {
            if (item && typeof item === 'object') {
              return item.msg ?? item.message ?? item.detail;
            }
            return item;
          })
          .map((message) => String(message ?? '').trim())
          .filter(Boolean)
          .join('; ');
        if (details) return details;
      } else if (data.detail.message) {
        return data.detail.message;
      }
    }
    // Check for the custom AppException format (error)
    if (data?.error) {
      return data.error;
    }
    // Check for message field
    if (data?.message) {
      return data.message;
    }
    if (axiosError.message) {
      return axiosError.message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}
